import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Eye, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { hr as hrLocale } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(date) {
  if (!date) return '';
  try { return formatDistanceToNow(new Date(date), { addSuffix: true, locale: hrLocale }); } catch { return ''; }
}

function formatTime(date) {
  if (!date) return '';
  try {
    const d = new Date(date);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
}

const CATEGORY_COLORS = {
  'Vijesti': '#1a56db', 'News': '#1a56db',
  'Sport': '#e02424', 'Sports': '#e02424',
  'Gospodarstvo': '#057a55', 'Business': '#057a55',
  'Tehnologija': '#7e3af2', 'Technology': '#7e3af2',
  'Kultura': '#d97706', 'Culture': '#d97706',
  'Zabava': '#ec4899', 'Entertainment': '#ec4899',
  'Zdravlje': '#10b981', 'Health': '#10b981',
  'Putovanja': '#0ea5e9', 'Travel': '#0ea5e9',
  'Politika': '#6366f1', 'Politics': '#6366f1',
  'Crna kronika': '#374151', 'Crime': '#374151',
  'Društvo': '#8b5cf6', 'Society': '#8b5cf6',
  'Aktualno': '#ef4444', 'Current': '#ef4444',
  'EU': '#2563eb',
  'Ekonomija': '#059669', 'Economy': '#059669',
  'Turizam': '#06b6d4', 'Tourism': '#06b6d4',
  'Regional': '#f59e0b',
};

/* ─── Main Hero Card (large, left side) ─── */
function MainHeroCard({ article }) {
  if (!article) return <div className="w-full h-full min-h-[400px] lg:min-h-[520px] bg-gray-200 dark:bg-navy-light rounded-lg animate-pulse" />;
  const catColor = CATEGORY_COLORS[article.category] || '#6366f1';
  return (
    <Link to={`/article/${article.slug}`} className="group relative block w-full h-full min-h-[400px] lg:min-h-[520px] overflow-hidden bg-navy" data-testid="hero-main-card">
      {article.featured_image && (
        <img src={article.featured_image} alt={article.title} loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      {article.is_breaking && (
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="px-3 py-1 bg-red-600 text-white text-[11px] font-bold tracking-widest uppercase flex items-center gap-1.5">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" /> UŽIVO
          </span>
        </div>
      )}
      {article.category && (
        <div className="absolute top-4 left-4" style={{ left: article.is_breaking ? '120px' : '16px' }}>
          <span className="px-2.5 py-1 text-white text-[10px] font-bold tracking-wider uppercase" style={{ backgroundColor: catColor }}>
            {article.category}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-8">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3 group-hover:text-gray-200 transition-colors line-clamp-3" data-testid="hero-main-title">
          {article.title}
        </h2>
        {article.excerpt && (
          <p className="text-gray-300 text-sm lg:text-base line-clamp-2 mb-3 max-w-2xl">{article.excerpt}</p>
        )}
        <div className="flex items-center gap-3 text-gray-400 text-xs">
          <span className="flex items-center gap-1"><Clock size={11} /> {timeAgo(article.published_at)}</span>
          {article.view_count > 0 && <span className="flex items-center gap-1"><Eye size={11} /> {article.view_count?.toLocaleString()}</span>}
        </div>
      </div>
    </Link>
  );
}

/* ─── Secondary Hero Card (medium, right column) ─── */
function SecondaryHeroCard({ article }) {
  if (!article) return <div className="h-full min-h-[200px] bg-gray-200 dark:bg-navy-light rounded-lg animate-pulse" />;
  const catColor = CATEGORY_COLORS[article.category] || '#6366f1';
  return (
    <Link to={`/article/${article.slug}`} className="group relative block h-full min-h-[200px] lg:min-h-[252px] overflow-hidden bg-navy" data-testid="hero-secondary-card">
      {article.featured_image && (
        <img src={article.featured_image} alt={article.title} loading="eager" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      {article.category && (
        <div className="absolute top-3 left-3">
          <span className="px-2 py-0.5 text-white text-[9px] font-bold tracking-wider uppercase" style={{ backgroundColor: catColor }}>
            {article.category}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="font-serif text-base lg:text-lg font-bold text-white leading-snug line-clamp-3 group-hover:text-gray-200 transition-colors">
          {article.title}
        </h3>
      </div>
    </Link>
  );
}

/* ─── Sidebar Tab Item ─── */
function TabArticleItem({ article }) {
  const catColor = CATEGORY_COLORS[article.category] || '#6366f1';
  return (
    <Link to={`/article/${article.slug}`} className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-white/5 last:border-0 group" data-testid="hero-tab-item">
      <span className="flex-shrink-0 text-xs font-mono text-gray-400 pt-0.5 w-10 text-right">{formatTime(article.published_at)}</span>
      <div className="flex-1 min-w-0">
        {article.category && (
          <span className="text-[9px] font-bold tracking-wider uppercase block mb-0.5" style={{ color: catColor }}>{article.category}</span>
        )}
        <h4 className="text-[13px] font-semibold text-navy dark:text-white leading-snug line-clamp-2 group-hover:text-sky-accent transition-colors">{article.title}</h4>
      </div>
    </Link>
  );
}

/* ═══ FEATURED HERO SECTION ═══ */
export default function FeaturedHeroSection({ loading: parentLoading, t, bundledData }) {
  const [heroArticles, setHeroArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [mostReadArticles, setMostReadArticles] = useState([]);
  const [activeTab, setActiveTab] = useState('najnovije');
  const [loading, setLoading] = useState(true);
  const [tabTransitioning, setTabTransitioning] = useState(false);
  const autoRotateRef = useRef(null);
  const TAB_ROTATE_MS = 12000;  // 12s between tab switches

  // Use bundled data from parent (single API call) instead of 3 separate calls
  useEffect(() => {
    if (bundledData) {
      setHeroArticles(bundledData.heroArticles || []);
      setLatestArticles(bundledData.latestArticles || []);
      setMostReadArticles(bundledData.mostReadArticles || []);
      setLoading(false);
    }
  }, [bundledData]);

  // Auto-rotate tabs
  useEffect(() => {
    autoRotateRef.current = setInterval(() => {
      setTabTransitioning(true);
      setTimeout(() => {
        setActiveTab(prev => prev === 'najnovije' ? 'najcitanije' : 'najnovije');
        setTabTransitioning(false);
      }, 200);
    }, TAB_ROTATE_MS);
    return () => clearInterval(autoRotateRef.current);
  }, []);

  const handleTabClick = (tab) => {
    // Reset auto-rotate on manual click
    clearInterval(autoRotateRef.current);
    setTabTransitioning(true);
    setTimeout(() => {
      setActiveTab(tab);
      setTabTransitioning(false);
    }, 200);
    autoRotateRef.current = setInterval(() => {
      setTabTransitioning(true);
      setTimeout(() => {
        setActiveTab(prev => prev === 'najnovije' ? 'najcitanije' : 'najnovije');
        setTabTransitioning(false);
      }, 200);
    }, TAB_ROTATE_MS);
  };

  const isLoading = loading || parentLoading;
  const mainArticle = heroArticles[0];
  const secondaryArticles = heroArticles.slice(1, 3);
  const tabArticles = activeTab === 'najnovije' ? latestArticles : mostReadArticles;

  const tabContentStyle = {
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    opacity: tabTransitioning ? 0 : 1,
    transform: tabTransitioning ? 'translateY(6px)' : 'translateY(0)',
  };

  return (
    <>
      {/* ─── Mobile Hero ─── */}
      <section className="lg:hidden mb-4" data-testid="mobile-featured-hero">
        <div className="mb-3">
          {isLoading ? <div className="w-full aspect-[16/9] bg-gray-200 dark:bg-navy-light rounded-lg animate-pulse" /> : <MainHeroCard article={mainArticle} />}
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {isLoading ? (
            <>
              <div className="aspect-[4/3] bg-gray-200 dark:bg-navy-light rounded-lg animate-pulse" />
              <div className="aspect-[4/3] bg-gray-200 dark:bg-navy-light rounded-lg animate-pulse" />
            </>
          ) : (
            secondaryArticles.map((a, i) => <SecondaryHeroCard key={a.slug || i} article={a} />)
          )}
        </div>
        <div className="bg-white dark:bg-navy-light rounded-xl border border-gray-100 dark:border-white/5">
          <div className="flex border-b border-gray-100 dark:border-white/10">
            <button onClick={() => handleTabClick('najnovije')} data-testid="mobile-tab-najnovije" className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'najnovije' ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-navy dark:hover:text-white'}`}>Najnovije</button>
            <button onClick={() => handleTabClick('najcitanije')} data-testid="mobile-tab-najcitanije" className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'najcitanije' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-navy dark:hover:text-white'}`}>Najčitanije</button>
          </div>
          <div className="px-4 py-2" style={tabContentStyle}>
            {tabArticles.slice(0, 4).map((a, i) => <TabArticleItem key={a.slug || i} article={a} />)}
          </div>
        </div>
      </section>

      {/* ─── Desktop Hero ─── */}
      <section className="hidden lg:grid grid-cols-12 gap-0 mb-8 overflow-hidden rounded-lg" data-testid="featured-hero-section">
        <div className="col-span-5">
          {isLoading ? <div className="w-full h-full min-h-[520px] bg-gray-200 dark:bg-navy-light animate-pulse" /> : <MainHeroCard article={mainArticle} />}
        </div>
        <div className="col-span-4 flex flex-col">
          <div className="flex-1">
            {isLoading ? <div className="w-full h-full bg-gray-200 dark:bg-navy-light animate-pulse" /> : <SecondaryHeroCard article={secondaryArticles[0]} />}
          </div>
          <div className="flex-1 border-t border-white/10">
            {isLoading ? <div className="w-full h-full bg-gray-200 dark:bg-navy-light animate-pulse" /> : <SecondaryHeroCard article={secondaryArticles[1]} />}
          </div>
        </div>
        <div className="col-span-3 bg-white dark:bg-navy-light flex flex-col" data-testid="hero-tabs-sidebar">
          <div className="flex border-b border-gray-100 dark:border-white/10 relative">
            <button onClick={() => handleTabClick('najnovije')} data-testid="tab-najnovije" className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'najnovije' ? 'bg-red-600 text-white' : 'bg-gray-50 dark:bg-navy text-gray-500 hover:text-navy dark:hover:text-white'}`}>
              Najnovije
            </button>
            <button onClick={() => handleTabClick('najcitanije')} data-testid="tab-najcitanije" className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'najcitanije' ? 'bg-gray-800 text-white' : 'bg-gray-50 dark:bg-navy text-gray-500 hover:text-navy dark:hover:text-white'}`}>
              Najčitanije
            </button>
            {/* Auto-rotate progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-200 dark:bg-gray-700">
              <div className="h-full bg-red-500 animate-hero-progress" style={{ animationDuration: `${TAB_ROTATE_MS}ms` }} key={activeTab} />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-2" style={tabContentStyle}>
            {isLoading ? (
              <div className="space-y-3 py-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-100 dark:bg-white/5 rounded animate-pulse" />)}</div>
            ) : (
              tabArticles.slice(0, 4).map((a, i) => <TabArticleItem key={a.slug || i} article={a} />)
            )}
          </div>
          <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-white/5">
            <Link to="/category/vijesti" className="flex items-center justify-center gap-1 text-[11px] font-bold text-red-600 hover:text-red-700 uppercase tracking-wider" data-testid="hero-load-more">
              Učitaj još <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-hero-progress {
          animation: heroProgress linear forwards;
        }
      `}</style>
    </>
  );
}

export { CATEGORY_COLORS, timeAgo };
